import React, { useState, createRef, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { fonts } from '../../DesignSystem/typography';
import { ImpFlex, colors } from '../..';
const shake = keyframes `
  0% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  50% { transform: translateX(10px); }
  75% { transform: translateX(-10px); }
  100% { transform: translateX(0); }
`;
const LETTER_SPACING_PX = 32;
const LETTER_SPACING = '32px';
// adjusts for difference in width of bullet and number
const BULLET_LETTER_SPACING = '37px';
const PLACEHOLDER_COLOR_ADJUSTMENT = '66';
const textStyles = `
  height: 56px;
  text-align: left;
  font-family: ${fonts.regular};
  font-size: 24px;
  font-style: normal;
  font-feature-settings: 'tnum' on, 'lnum' on, 'zero' on;
  line-height: 32px;
  margin: 0px;
  padding: 0px;
  outline: none;
  border: none;
`;
const calculateWidth = (numInputs) => `calc(${LETTER_SPACING_PX * numInputs}px + ${numInputs * 15}px)`;
const cursorForFull = (isFull) => (isFull ? 'cursor: pointer;' : '');
const cursorAndColorForDisabled = (isDisabled) => isDisabled
    ? `
    cursor: not-allowed;
    color: ${colors.content.onSurfaceDisabled};
  `
    : '';
const placeholderColor = (showError) => showError
    ? `${colors.content.onSurfaceError}`
    : `${colors.content.onSurfaceDisabled}`;
const getLetterSpacing = (hasContent) => hasContent ? LETTER_SPACING : BULLET_LETTER_SPACING;
const getBackgroundColor = ({ isDisabled, showError, }) => {
    if (isDisabled) {
        return colors.surface.surfaceDisabled;
    }
    if (showError) {
        return colors.surface.surfaceError;
    }
    return colors.surface.surfaceHigh;
};
const getBorderColor = ({ isDisabled, showError, isFocused, }) => {
    if (isDisabled) {
        return colors.border.borderDisabled;
    }
    if (showError) {
        return isFocused
            ? colors.border.borderErrorFocus
            : colors.border.borderError;
    }
    return isFocused ? colors.border.borderFocus : colors.border.borderVariant;
};
const Input = styled.input `
  ${textStyles}
  color: ${colors.content.onSurface};
  background-color: transparent;
  padding-left: ${LETTER_SPACING};
  letter-spacing: ${(props) => getLetterSpacing(props.hasContent)};
  border: 0;
  animation: ${(props) => props.showError
    ? css `
          ${shake} 0.4s ease-in-out
        `
    : 'none'};

  width: ${(props) => calculateWidth(props.numInputs)};
  ${(props) => cursorForFull(props.isFull)}
  ${(props) => cursorAndColorForDisabled(props.isDisabled)}

  ::placeholder {
    color: ${(props) => placeholderColor(props.showError)};
  }

  transition: background-color 0.1s, border 0.1s;
  border-radius: 8px;
  border: 1px solid ${(props) => getBorderColor({ isFocused: false, ...props })};

  background-color: ${(props) => getBackgroundColor(props)};

  :focus {
    border-color: ${(props) => getBorderColor({ isFocused: true, ...props })};
  }

  ${(props) => !props.isDisabled &&
    `&:not(:focus):hover {
    border-color: ${getBorderColor({ isFocused: true, ...props })};
    cursor: pointer;
    }`}
`;
/**
 * Component for rendering an OTP (One-Time Password) input field. This is commonly used for PIN and OTP entry.
 *
 * @component
 * @example
 * <ImpOtpField
 *   numInputs={6}
 *   onChange={(otp) => console.log(otp)}
 *   onFull={(otp) => console.log(`Full OTP: ${otp}`)}
 *   mask={false}
 *   autoFocus={false}
 *   clearWithError={false}
 *   clear={false}
 * />
 **/
export default function ImpOtpField({ numInputs = 6, onChange, onFull, mask = false, autoFocus = false, clearWithError = false, clear = false, autoComplete = 'one-time-code', }) {
    const [otp, setOtp] = useState('');
    const [showError, setShowError] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const ref = createRef();
    const focusInput = () => {
        var _a;
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const clearInput = () => {
        setIsDisabled(false);
        setOtp('');
        focusInput();
    };
    useEffect(() => {
        if (autoFocus) {
            focusInput();
        }
    }, [isDisabled]);
    useEffect(() => {
        var _a;
        if (otp.length !== 0) {
            setShowError(false);
        }
        if (otp.length === numInputs) {
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.blur();
            setIsDisabled(true);
            onFull(otp);
        }
    }, [otp]);
    useEffect(() => {
        if (clearWithError) {
            clearInput();
            setShowError(true);
        }
        else {
            setShowError(false);
        }
    }, [clearWithError]);
    useEffect(() => {
        if (clear) {
            clearInput();
            setShowError(false);
        }
    }, [clear]);
    const handleChange = (e) => {
        const value = e.target.value;
        const numericValue = value.replace(/[^0-9]/g, '');
        setOtp(numericValue);
        onChange && onChange(numericValue);
    };
    return (React.createElement(ImpFlex, { direction: "row", justify: "center", fluid: true },
        React.createElement(Input, { value: otp, disabled: isDisabled, isDisabled: isDisabled, showError: showError, numInputs: numInputs, ref: ref, type: mask ? 'password' : 'text', autoComplete: autoComplete, pattern: "[0-9]*", inputMode: "numeric", maxLength: numInputs, placeholder: '•'.repeat(numInputs), isFull: otp.length === numInputs, onChange: handleChange, autoFocus: autoFocus, hasContent: otp.length > 0 && !mask })));
}
//# sourceMappingURL=ImpOtpField.js.map